const IMPRESSION_TYPE_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-clinical-impression-type';
const IMPRESSION_CITATION_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-citations';
const IMPRESSION_CITATION_ID_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-citation-id';

export const IMPRESSION_AI_GENERATED =
  'https://lifeomic.com/fhir/fountain-life-ai-generated';

const TEMPLATE_ID_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-impression-template-id';

const TEMPLATE_DURABLE_ID_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-impression-template-durable-id';

const TRACE_ID_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-ai-generated-trace-id';

const REVIEW_DIFF_DOCUMENT_SYSTEM =
  'https://lifeomic.com/fhir/review-diff-document';

import { Endpoints } from '@lifeomic/react-client/generated/ai-template-service';

import moment from 'moment';
import { v4 } from 'uuid';
import {
  SYSTEM_ACTION_PLAN_LAST_EDITED_BY,
  SYSTEM_LAST_EDITED_BY,
} from '../../../../../util/fhir';
import { EXECUTIVE_SUMMARY_IMPRESSION_TYPES } from '../../ExecutiveSummary/constants';
import { isAiGenerated } from '../../ExecutiveSummary/utils';
import {
  getCitationIdPrefix,
  isDocumentReference,
  isFhirReference,
} from '../AttachmentArbiter/Attachment';

/** These are all the kinds we have iconography/text for */
export const KNOWN_IMPRESSION_TYPES: readonly NonNullable<
  Extract<
    Endpoints['GET /v1/ai-templates/:id']['Response']['template'],
    { type: 'template' }
  >['impressionType']
>[] = [
  ...EXECUTIVE_SUMMARY_IMPRESSION_TYPES,
  'general',
  'synopsis',
  'nutrition',
  'supplements',
];

export type KnownImpressionTypes = (typeof KNOWN_IMPRESSION_TYPES)[number];

export class ImpressionItem {
  constructor(public impression: fhir.ClinicalImpression) {}

  get id() {
    return this.impression.id!;
  }

  get title() {
    return this.impression.description;
  }

  get summary() {
    return this.impression.summary;
  }

  get findings() {
    return (this.impression.finding ?? []).map(
      finding => finding.itemReference?.display ?? '',
    );
  }

  get attachments() {
    return (
      this.impression.investigation?.find(
        investigation =>
          investigation.code.text === IMPRESSION_CITATION_SYSTEM ||
          investigation.code.coding?.some(
            coding => coding.system === IMPRESSION_CITATION_SYSTEM,
          ),
      )?.item ?? []
    );
  }

  get templateId() {
    // look for the template durable id first, then fall back to the template id
    return (
      this.impression.meta?.tag?.find(
        tag => tag.system === TEMPLATE_DURABLE_ID_SYSTEM,
      )?.code ??
      this.impression.meta?.tag?.find(tag => tag.system === TEMPLATE_ID_SYSTEM)
        ?.code ??
      ''
    );
  }

  get status() {
    return this.impression.status;
  }

  get lastEdited() {
    return this.impression.meta?.lastUpdated
      ? moment(this.impression.meta.lastUpdated).format('MM/DD/YYYY hh:mm A')
      : undefined;
  }

  get reportDate(): string | undefined {
    const date = this.impression.date ?? this.impression.meta?.lastUpdated;
    return date ? moment(date).format('MMM D YYYY') : undefined;
  }

  get lastEditedBy() {
    if (isAiGenerated(this.impression)) {
      return 'Zori';
    }
    return this.getLastEditedBy();
  }

  get aiGenerated() {
    return isAiGenerated(this.impression);
  }

  get traceId() {
    return this.impression.extension?.find(ext => ext.url === TRACE_ID_SYSTEM)
      ?.valueString;
  }

  get reviewDiffDocument(): string | undefined {
    return this.impression.extension?.find(
      ext => ext.url === REVIEW_DIFF_DOCUMENT_SYSTEM,
    )?.valueString;
  }

  addAttachment(
    attachment: fhir.Resource | fhir.Reference,
  ): fhir.Reference | undefined {
    let reference: fhir.Reference;
    if (isFhirReference(attachment)) {
      reference = { ...attachment };
    } else {
      const type = attachment.resourceType!;
      const id = attachment.id!;

      reference = {
        reference: `${type}/${id}`,
      };
    }

    const citationIdPrefix = getCitationIdPrefix(attachment);

    if (!citationIdPrefix) {
      console.error('Adding attachment type is not supported', attachment);
      return;
    }
    const nextId = `${citationIdPrefix}${v4()}`;

    reference = {
      ...reference,
      identifier: {
        system: IMPRESSION_CITATION_ID_SYSTEM,
        value: `${citationIdPrefix}${nextId}`,
      },
      extension: isDocumentReference(attachment)
        ? [
            {
              url: 'http://lifeomic.com/fhir/document-type',
              valueString: 'clinician-provided', // TODO: make this dynamic
            },
          ]
        : [],
    };

    const idx = this.impression.investigation?.findIndex(
      inv => inv.code.text === IMPRESSION_CITATION_SYSTEM,
    );

    // update the existing investigation if it exists
    if (idx !== -1 && idx !== undefined && this.impression.investigation) {
      this.impression.investigation[idx] = {
        ...this.impression.investigation[idx],
        item: [...(this.impression.investigation[idx].item || []), reference],
      };
    } else if (this.impression.investigation) {
      // add a new investigation if it doesn't exist, but there is another investigation
      this.impression.investigation.push({
        code: { text: IMPRESSION_CITATION_SYSTEM },
        item: [reference],
      });
    } else {
      // add a new investigation if it doesn't exist
      this.impression.investigation = [
        {
          code: { text: IMPRESSION_CITATION_SYSTEM },
          item: [reference],
        },
      ];
    }

    return reference;
  }

  get impressionType(): KnownImpressionTypes {
    return (
      this.impression.code?.coding?.find(
        c => c.system === IMPRESSION_TYPE_SYSTEM,
      )?.code ?? 'general'
    ).toLowerCase() as KnownImpressionTypes;
  }

  getAttachmentByIdentifier(identifier: string) {
    return this.attachments.find(attachment => {
      return (
        attachment.identifier?.system === IMPRESSION_CITATION_ID_SYSTEM &&
        attachment.identifier.value === identifier
      );
    });
  }

  /**
   * Used to deduplicate attachments with different identifiers and with the same reference.
   * Primarily used for expanded view, to prevent embedding the same attachment multiple times.
   * when the citation id is different, but points to the same attachment.
   * */
  getDeduplicatedAttachmentByIdentifier(identifier: string) {
    const deduplicatedAttachments = this.attachments.reduce(
      (acc, attachment) => {
        if (acc.findIndex(a => a.reference === attachment.reference) === -1) {
          acc.push(attachment);
        }
        return acc;
      },
      [] as fhir.Reference[],
    );
    return deduplicatedAttachments.find(attachment => {
      return (
        attachment.identifier?.system === IMPRESSION_CITATION_ID_SYSTEM &&
        attachment.identifier.value === identifier
      );
    });
  }

  getLastEditedBy() {
    return (
      // look for the last edited by extension, first in the new system, then in the old system
      this.impression.extension?.find(ext => ext.url === SYSTEM_LAST_EDITED_BY)
        ?.valueString ??
      this.impression.extension?.find(
        ext => ext.url === SYSTEM_ACTION_PLAN_LAST_EDITED_BY,
      )?.valueString ??
      ''
    );
  }
}
